'use client';

import { create } from 'zustand';
import type { Id } from 'convex/_generated/dataModel';

export const DEFAULT_PLANT_EMOJI = '🌱';

interface PlantsStore {
  createDialogOpen: boolean;
  editDialogOpen: boolean;
  deleteDialogOpen: boolean;
  editingPlantId: Id<'userPlants'> | null;
  plantToDelete: Id<'userPlants'> | null;
  draftName: string;
  draftEmoji: string;
  draftCategory: string;
  searchQuery: string;
  selectedCategory: string | null;
  isSaving: boolean;

  setIsSaving: (isSaving: boolean) => void;
  setSearchQuery: (query: string) => void;
  setSelectedCategory: (category: string | null) => void;
  startCreating: (defaultCategory: string) => void;
  cancelCreating: () => void;
  startEditing: (
    plantId: Id<'userPlants'>,
    name: string,
    emoji: string,
    category: string
  ) => void;
  cancelEditing: () => void;
  confirmDelete: (plantId: Id<'userPlants'>) => void;
  cancelDelete: () => void;
  setDraftName: (name: string) => void;
  setDraftEmoji: (emoji: string) => void;
  setDraftCategory: (category: string) => void;
  resetDrafts: () => void;
}

function resetDraftFields() {
  return {
    draftName: '',
    draftEmoji: DEFAULT_PLANT_EMOJI,
    draftCategory: '',
  };
}

export const usePlantsStore = create<PlantsStore>((set) => ({
  createDialogOpen: false,
  editDialogOpen: false,
  deleteDialogOpen: false,
  editingPlantId: null,
  plantToDelete: null,
  ...resetDraftFields(),
  searchQuery: '',
  selectedCategory: null,
  isSaving: false,

  setIsSaving: (isSaving) => set({ isSaving }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setSelectedCategory: (category) => set({ selectedCategory: category }),
  startCreating: (defaultCategory) =>
    set({
      createDialogOpen: true,
      ...resetDraftFields(),
      draftCategory: defaultCategory,
    }),
  cancelCreating: () => set({ createDialogOpen: false, ...resetDraftFields() }),
  startEditing: (plantId, name, emoji, category) =>
    set({
      editingPlantId: plantId,
      editDialogOpen: true,
      draftName: name,
      draftEmoji: emoji,
      draftCategory: category,
    }),
  cancelEditing: () =>
    set({ editingPlantId: null, editDialogOpen: false, ...resetDraftFields() }),
  confirmDelete: (plantId) => set({ plantToDelete: plantId, deleteDialogOpen: true }),
  cancelDelete: () => set({ plantToDelete: null, deleteDialogOpen: false }),
  setDraftName: (name) => set({ draftName: name }),
  setDraftEmoji: (emoji) => set({ draftEmoji: emoji }),
  setDraftCategory: (category) => set({ draftCategory: category }),
  resetDrafts: () => set(resetDraftFields()),
}));
